import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  HostListener,
  inject,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  viewChild,
  ViewEncapsulation,
} from '@angular/core';
import { ReactiveFormsModule, UntypedFormControl } from '@angular/forms';
import {
  MAT_AUTOCOMPLETE_DEFAULT_OPTIONS,
  MatAutocomplete,
  MatAutocompleteDefaultOptions,
  MatAutocompleteModule,
  MatAutocompleteTrigger,
} from '@angular/material/autocomplete';
import { MatOptionModule } from '@angular/material/core';
import { MatIconModule } from '@angular/material/icon';
import { MatInput } from '@angular/material/input';
import { Router, RouterModule } from '@angular/router';

import { first } from 'lodash-es';
import { BehaviorSubject, concat, Observable, of, Subject } from 'rxjs';
import { debounceTime, distinctUntilChanged, filter, takeUntil, tap, withLatestFrom } from 'rxjs/operators';

import { SelectableTopic } from '../../../extract/constans/default-topics';
import { HelpTriggerDirective } from '../../../help/directives/help-trigger.directive';
import { Transaction } from '../../../home/models/transaction.model';
import { DEFAULT_COLUMN_WIDTH_DT } from '../../../market-comparison/constants/default-column';
import { getColumnByDatapoint } from '../../../market-comparison/constants/get-column-by-datapoint';
import { getColumnId } from '../../../market-comparison/constants/get-column-id';
import { ColumnType } from '../../../market-comparison/models/column-type.enum';
import { Column, TopicId, TransactionId } from '../../../market-comparison/models/column.model';
import { DefinedTermMeta } from '../../../market-comparison/models/defined-term-meta.model';
import { SemerisRoutes } from '../../../shared/constants/routes';
import { PermissionsDirective } from '../../../shared/directives/permissions.directive';
import { Permission } from '../../../shared/models/api/permissions.enum';
import { PanelAction, PanelCommand } from '../../../shared/models/common/command';
import { IPanelComponent } from '../../../shared/models/common/i-panel.component';
import { NgChanges } from '../../../shared/models/common/ng-changes';
import { TruncatePipe } from '../../../shared/pipes/truncate.pipe';
import { ifChanged } from '../../../shared/utils/if-changed';
import { DatapointField } from '../../../unified-datapoint/models/datapoint-field.models';
import { DatapointRule } from '../../../unified-datapoint/models/datapoint-rule.model';
import { SearchResult, SearchResultType, UnifiedSearchResult } from '../../models/search.model';
import { SearchResultHeaderComponent } from '../search-result-header/search-result-header.component';
import { SearchResultItemComponent } from '../search-result-item/search-result-item.component';
import { TransactionResultItemComponent } from '../transaction-result-item/transaction-result-item.component';

const panelBackdropClass = 'search-box-panel-backdrop';

@Component({
  imports: [
    CommonModule,
    MatIconModule,
    MatOptionModule,
    ReactiveFormsModule,
    MatAutocompleteModule,
    RouterModule,
    SearchResultItemComponent,
    SearchResultHeaderComponent,
    TruncatePipe,
    TransactionResultItemComponent,
    HelpTriggerDirective,
    PermissionsDirective,
    MatInput,
  ],
  selector: 'semeris-search-box',
  templateUrl: './search-box.component.html',
  styleUrls: ['./search-box.component.scss'],
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: { class: 'h-full bg-surface overflow-hidden will-change-transform' },
  providers: [
    {
      provide: MAT_AUTOCOMPLETE_DEFAULT_OPTIONS,
      useValue: {
        autoActiveFirstOption: true,
        overlayPanelClass: panelBackdropClass,
      } as MatAutocompleteDefaultOptions,
    },
  ],
})
export class SearchBoxComponent implements OnInit, OnDestroy, IPanelComponent, OnChanges {
  private readonly router = inject(Router);

  @Input() public results: SearchResult;
  @Input() public loading: boolean;
  @Input() public transactionLookup: Record<string, UnifiedSearchResult<Transaction>[]>;
  @Input() public disabled: boolean;

  @Output() public filterChange = new EventEmitter<string>();
  @Output() public selected = new EventEmitter<any>();
  @Output() public addToCompare = new EventEmitter<TransactionId>();
  @Output() public removeFromComparison = new EventEmitter<TransactionId>();
  @Output() public toggleTopic = new EventEmitter<TopicId>();

  @Output() public marketCompare = new EventEmitter<Column>();
  @Output() public reset = new EventEmitter<void>();

  @HostListener('keydown', ['$event'])
  public onKeyDown(ev: KeyboardEvent) {
    if (
      ev.key.toLowerCase() === 'escape' ||
      (['backspace', 'delete'].includes(ev.key.toLowerCase()) && !this.control.value)
    ) {
      this.close();
      this.resetSearch();
    }
  }

  public text$: Observable<string>;

  public readonly SemerisRoutes = SemerisRoutes;
  public readonly Permission = Permission;
  public readonly fullTextResult: UnifiedSearchResult = {
    type: SearchResultType.FullTextSearch,
    title: 'Full text search',
    routerLink: ['/', SemerisRoutes.search.documentSearch],
  };

  public control = new UntypedFormControl('');
  public exactTransactionMatch$ = new BehaviorSubject<UnifiedSearchResult<Transaction>>(undefined);

  private completed$ = new Subject();

  public readonly autocompleteTriggerComponent = viewChild(MatAutocompleteTrigger);
  public readonly autocompleteComponent = viewChild(MatAutocomplete);

  public get searchHint(): string {
    return `Search for "${this.control.value}"`;
  }

  public ngOnChanges(changes: NgChanges<SearchBoxComponent>): void {
    ifChanged([changes.results, changes.loading], () => {
      this.checkExactMatch();
    });
  }

  public onOpened() {
    const panelEl: HTMLDivElement = this.autocompleteComponent()?.panel?.nativeElement;
    panelEl?.parentElement?.addEventListener('click', (ev: MouseEvent) => {
      if ((ev.target as HTMLElement).className?.includes(panelBackdropClass)) {
        this.autocompleteTriggerComponent().closePanel();
      }
    });
  }

  public handleCommand(command: PanelAction): void {
    if (command.command === PanelCommand.Open) {
      setTimeout(() => {
        this.control.setValue(new Date().getFullYear().toString());
        this.autocompleteTriggerComponent().connectedTo?.elementRef?.nativeElement?.focus();
      }, 0);
    }

    if (command.command === PanelCommand.Close && this.autocompleteTriggerComponent()?.panelOpen) {
      this.close();
      this.resetSearch();
    }
  }

  public onToggleTopic(item: UnifiedSearchResult<SelectableTopic>) {
    this.toggleTopic.emit(item.item.id);
  }

  public ngOnInit(): void {
    this.text$ = concat(of(this.control.value), this.control.valueChanges).pipe(
      filter((v) => typeof v === 'string'),
      tap((v) => {
        this.checkExactMatch();
      })
    );

    this.control.valueChanges
      .pipe(takeUntil(this.completed$), distinctUntilChanged(), debounceTime(250), withLatestFrom(this.text$))
      .subscribe(([value, text]: [UnifiedSearchResult, string]) => {
        if (!value) {
          this.reset.emit();
          return;
        }

        if (typeof value === 'string') {
          this.filterChange.emit(value);
        } else {
          if (value?.type === SearchResultType.DefinedTerm) {
            this.onMarketCompareDefinedTerm(value as UnifiedSearchResult<string>);
            return;
          }

          if (value?.type === SearchResultType.BuiltInTopic) {
            return;
          }

          if (value?.type === SearchResultType.Datapoint) {
            this.onMarketCompareDatapoint(value as UnifiedSearchResult<DatapointField | DatapointRule>);
            return;
          }

          if (value?.type === SearchResultType.FullTextSearch) {
            this.router.navigate(value.routerLink, { queryParams: { q: text } });

            this.close();
            this.resetSearch();

            return;
          }

          this.router.navigate(value.routerLink, { queryParams: value.queryParams });

          this.close();
          this.resetSearch();
        }
      });
  }

  public onMarketCompareDefinedTerm(item: UnifiedSearchResult) {
    const column: Column<DefinedTermMeta> = {
      type: ColumnType.DefineTerm,
      width: DEFAULT_COLUMN_WIDTH_DT,
      value: {
        term: item.title,
        relatedTerms: [],
        synonyms: [],
      },
    };

    this.marketCompare.emit({
      ...column,
      id: getColumnId(column),
    });

    this.close();
    this.resetSearch();
  }

  public onMarketCompareDatapoint(item: UnifiedSearchResult) {
    const ruleBase = item.item as DatapointField | DatapointRule;
    this.marketCompare.emit(getColumnByDatapoint(ruleBase));

    this.close();
    this.resetSearch();
  }

  public resetSearch() {
    this.control.setValue('');
  }

  public close() {
    this.autocompleteTriggerComponent().closePanel();
  }

  public onAddToCompare(item: TransactionId) {
    this.addToCompare.emit(item);
  }

  public onRemoveFromComparison(item: TransactionId) {
    this.removeFromComparison.emit(item);
  }

  public displayFn(item) {
    return typeof item === 'string' ? item : '';
  }

  public ngOnDestroy() {
    this.completed$.next(undefined);
    this.completed$.complete();
  }

  private checkExactMatch() {
    let result: UnifiedSearchResult<Transaction>;

    if (typeof this.control.value === 'string' && this.transactionLookup) {
      const resultByLookup = this.transactionLookup[this.control.value.toLowerCase()];
      result = resultByLookup?.length === 1 ? first(resultByLookup) : undefined;
    }

    if (this.results?.transactions?.length === 1) {
      result = first(this.results.transactions);
    }

    this.exactTransactionMatch$.next(result);
  }
}
